import { HiOutlineDocumentText, HiOutlineSquares2X2 } from 'react-icons/hi2'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { NavigationOption } from '@/types/navigation/Sidebar'

export type SidebarVariant = 'standard' | 'legacy'

export interface SidebarStore {
  // Basic sidebar state
  expanded: boolean
  visible: boolean
  variant: SidebarVariant

  // Basic sidebar actions
  setExpanded: (expanded: boolean) => void
  setVisible: (visible: boolean) => void
  toggleSidebar: () => void
  setVariant: (variant: SidebarVariant) => void

  // Flyout state
  activeFlyoutId: string | null
  delayedFlyoutId: string | null
  flyoutVisible: boolean
  hoverSuppressed: boolean

  // Flyout actions
  setActiveFlyoutId: (id: string | null) => void
  setDelayedFlyoutId: (id: string | null) => void
  setFlyoutVisible: (visible: boolean) => void
  setHoverSuppressed: (suppressed: boolean) => void
  closeFlyout: () => void
  closeFlyoutAndSuppressHover: () => void
  clearHoverSuppression: () => void
}

export const TASKS_OPTION: NavigationOption = {
  id: 'tasks',
  name: 'My Tasks',
  href: '/tasks?status=todo&status=in_progress&status=in_review',
  icon: HiOutlineSquares2X2,
  current: false,
  isTrialEligible: true,
  isOrganizationFeature: false,
  minOrganizationTier: 'free',
  match: (pathname: string) => pathname.startsWith('/tasks'),
}

export const DOCUMENTS_OPTION: NavigationOption = {
  id: 'documents',
  name: 'My Proposal Documents',
  href: '/documents?order_by=last_modified&sort=desc',
  icon: HiOutlineDocumentText,
  current: false,
  isTrialEligible: true,
  isOrganizationFeature: false,
  minOrganizationTier: 'free',
  match: (pathname: string) => pathname.startsWith('/documents'),
}

export const useSidebarStore = create<SidebarStore>()(
  persist(
    (set) => ({
      expanded: false,
      visible: false,
      variant: 'standard',

      setExpanded: (expanded: boolean) => {
        set((state) => ({ ...state, expanded }))
      },
      setVisible: (visible: boolean) => {
        set((state) => ({ ...state, visible }))
      },
      toggleSidebar: () =>
        set((state) => ({ ...state, visible: !state.visible })),
      setVariant: (variant: SidebarVariant) => {
        set((state) => ({ ...state, variant }))
      },

      activeFlyoutId: null,
      delayedFlyoutId: null,
      flyoutVisible: false,
      hoverSuppressed: false,

      setActiveFlyoutId: (id: string | null) => {
        set((state) => ({ ...state, activeFlyoutId: id }))
      },
      setDelayedFlyoutId: (id: string | null) => {
        set((state) => ({ ...state, delayedFlyoutId: id }))
      },
      setFlyoutVisible: (visible: boolean) => {
        set((state) => ({ ...state, flyoutVisible: visible }))
      },
      setHoverSuppressed: (suppressed: boolean) => {
        set((state) => ({ ...state, hoverSuppressed: suppressed }))
      },
      closeFlyout: () => {
        set((state) => ({
          ...state,
          activeFlyoutId: null,
          flyoutVisible: false,
        }))
      },
      closeFlyoutAndSuppressHover: () => {
        set((state) => ({
          ...state,
          activeFlyoutId: null,
          flyoutVisible: false,
          hoverSuppressed: true,
        }))
      },
      clearHoverSuppression: () => {
        set((state) => ({ ...state, hoverSuppressed: false }))
      },
    }),
    {
      name: 'sidebar-preferences',
      partialize: (state) => ({
        variant: state.variant,
        expanded: state.expanded,
      }),
    },
  ),
)

const selectActiveFlyoutId = (state: SidebarStore) => state.activeFlyoutId
export const useSidebarActiveFlyoutId = () => {
  return useSidebarStore(selectActiveFlyoutId)
}

const selectSetActiveFlyoutId = (state: SidebarStore) => state.setActiveFlyoutId
export const useSidebarSetActiveFlyoutId = () => {
  return useSidebarStore(selectSetActiveFlyoutId)
}

const selectFlyoutVisible = (state: SidebarStore) => state.flyoutVisible
export const useSidebarFlyoutVisible = () => {
  return useSidebarStore(selectFlyoutVisible)
}

const selectDelayedFlyoutId = (state: SidebarStore) => state.delayedFlyoutId
export const useSidebarDelayedFlyoutId = () => {
  return useSidebarStore(selectDelayedFlyoutId)
}

const selectSetDelayedFlyoutId = (state: SidebarStore) =>
  state.setDelayedFlyoutId
export const useSidebarSetDelayedFlyoutId = () => {
  return useSidebarStore(selectSetDelayedFlyoutId)
}

const selectSetFlyoutVisible = (state: SidebarStore) => state.setFlyoutVisible
export const useSidebarSetFlyoutVisible = () => {
  return useSidebarStore(selectSetFlyoutVisible)
}

const selectCloseFlyout = (state: SidebarStore) => state.closeFlyout
export const useSidebarCloseFlyout = () => {
  return useSidebarStore(selectCloseFlyout)
}

const selectCloseFlyoutAndSuppressHover = (state: SidebarStore) =>
  state.closeFlyoutAndSuppressHover
export const useSidebarCloseFlyoutAndSuppressHover = () => {
  return useSidebarStore(selectCloseFlyoutAndSuppressHover)
}

const selectHoverSuppressed = (state: SidebarStore) => state.hoverSuppressed
export const useSidebarHoverSuppressed = () => {
  return useSidebarStore(selectHoverSuppressed)
}

const selectSetHoverSuppressed = (state: SidebarStore) =>
  state.setHoverSuppressed
export const useSidebarSetHoverSuppressed = () => {
  return useSidebarStore(selectSetHoverSuppressed)
}

const selectClearHoverSuppression = (state: SidebarStore) =>
  state.clearHoverSuppression
export const useSidebarClearHoverSuppression = () => {
  return useSidebarStore(selectClearHoverSuppression)
}

const selectVariant = (state: SidebarStore) => state.variant
export const useSidebarVariant = () => {
  return useSidebarStore(selectVariant)
}

const selectSetVariant = (state: SidebarStore) => state.setVariant
export const useSidebarSetVariant = () => {
  return useSidebarStore(selectSetVariant)
}
